/**
 * Board or Table: which view the CRM opens in.
 *
 * Table is the default - it is the directory the desk already knows - and the
 * board is one click away. The last choice is remembered per signed-in user in
 * this browser. Storage is a convenience, never load-bearing: a private window
 * or a browser that refuses it falls back to Table, never to a blank page. An
 * explicit `?layout=board|table` in the URL wins for that visit.
 */
import { useCallback, useState } from 'react'

import { useLocalAuth } from '@/auth/LocalAuthContext'

export type CrmLayout = 'table' | 'board'

export const DEFAULT_CRM_LAYOUT: CrmLayout = 'table'
const STORAGE_PREFIX = 'cmos.crm.layout.v1'

export function isCrmLayout(value: unknown): value is CrmLayout {
  return value === 'table' || value === 'board'
}

export function layoutStorageKey(userId: string | null | undefined): string {
  return `${STORAGE_PREFIX}:${userId || 'anonymous'}`
}

export function readStoredLayout(userId: string | null | undefined): CrmLayout | null {
  try {
    const raw = window.localStorage.getItem(layoutStorageKey(userId))
    return isCrmLayout(raw) ? raw : null
  } catch {
    return null
  }
}

export function useCrmLayout(urlOverride?: string | null): [CrmLayout, (next: CrmLayout) => void] {
  const { localUser } = useLocalAuth()
  const userId = localUser?.id
  const [layout, setLayoutState] = useState<CrmLayout>(() =>
    isCrmLayout(urlOverride) ? urlOverride : (readStoredLayout(userId) ?? DEFAULT_CRM_LAYOUT),
  )

  const setLayout = useCallback(
    (next: CrmLayout) => {
      setLayoutState(next)
      try {
        window.localStorage.setItem(layoutStorageKey(userId), next)
      } catch {
        /* storage unavailable - the choice lives for this visit only */
      }
    },
    [userId],
  )

  return [layout, setLayout]
}

const TODAY_PREFIX = 'cmos.crm.today.v1'

/**
 * Whether the Today strip is folded away. A convenience for short screens,
 * remembered per user in this browser the same way the layout is; storage that
 * is missing or refused leaves the strip open.
 */
export function useTodayStripCollapsed(): [boolean, (next: boolean) => void] {
  const { localUser } = useLocalAuth()
  const key = `${TODAY_PREFIX}:${localUser?.id || 'anonymous'}`
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(key) === 'collapsed'
    } catch {
      return false
    }
  })
  const setCollapsed = useCallback(
    (next: boolean) => {
      setCollapsedState(next)
      try {
        window.localStorage.setItem(key, next ? 'collapsed' : 'open')
      } catch {
        /* storage unavailable - the choice lives for this visit only */
      }
    },
    [key],
  )
  return [collapsed, setCollapsed]
}
